import type { MentionEntity } from '@terra/shared-dto-chat-types';

export type SidebarTab =
  | 'dashboard'
  | 'network-graph'
  | 'signals'
  | 'assets'
  | 'findings'
  | 'reports'
  | 'executions'
  | 'code'
  | 'skills'
  | 'requests'
  | 'websocket'
  | 'intercept'
  | 'match-replace'
  | 'replay'
  | 'comparer'
  | 'interact'
  | 'agent-executions'
  | 'proxy-settings'
  | 'settings';
export type MainContentTab =
  | 'welcome'
  | 'application'
  | 'endpoint'
  | 'unverified-endpoint'
  | 'domain'
  | 'webpage'
  | 'settings'
  | 'requests'
  | 'websocket'
  | 'intercept'
  | 'match-replace'
  | 'replay'
  | 'proxy-settings'
  | 'comparer'
  | 'executions'
  | 'agent-execution'
  | 'signal'
  | 'finding'
  | 'interact'
  | 'signals-list'
  | 'findings-list'
  | 'reports-list'
  | 'executions-list'
  | 'assets-list'
  | 'code-list'
  | 'skills-list'
  | 'skill'
  | 'skill-editor'
  | 'hitl-edit'
  | 'http-viewer'
  | 'node-args-viewer'
  | 'browser-stream'
  | 'plan-viewer'
  | 'agent-executions-list'
  | 'co-pilot-execution'
  | 'surface-asset'
  | 'ai-component'
  | 'network-graph';

export const FINDING_MAIN_CONTENT_TAB_TYPE =
  'finding' as const satisfies MainContentTab;

export type FindingTabMode = 'view' | 'edit';

export const FINDING_TAB_MODE = {
  VIEW: 'view',
  EDIT: 'edit',
} as const satisfies Record<string, FindingTabMode>;

export const EPHEMERAL_TAB_TYPES: MainContentTab[] = [
  'hitl-edit',
  'http-viewer',
  'node-args-viewer',
  'browser-stream',
  'plan-viewer',
  'skill-editor',
];

export const APPLICATION_SPECIFIC_TAB_TYPES: MainContentTab[] = [
  'application',
  'signals-list',
  'findings-list',
  'reports-list',
  'executions-list',
  'assets-list',
  'code-list',
  'skills-list',
  'skill',
  'skill-editor',
  'agent-executions-list',
  'network-graph',
  'endpoint',
  'unverified-endpoint',
  'domain',
  'webpage',
  'executions',
  'agent-execution',
  'co-pilot-execution',
  'signal',
  'finding',
  'surface-asset',
  'ai-component',
  'hitl-edit',
  'http-viewer',
  'node-args-viewer',
  'browser-stream',
];

export type EndpointTab =
  | 'scoping'
  | 'details'
  | 'requests'
  | 'signals'
  | 'findings'
  | 'executions'
  | 'assets'
  | 'code'
  | 'reports'
  | 'memories'
  | 'webpages';
export type WebPageTab =
  | 'details'
  | 'signals'
  | 'findings'
  | 'executions'
  | 'endpoints'
  | 'features';

// Tab grouping types
export type TabGroupType =
  | 'flow'
  | 'findings'
  | 'signals'
  | 'executions'
  | 'custom';

export type TabGroupColor =
  | 'gray'
  | 'yellow'
  | 'red'
  | 'green'
  | 'blue'
  | 'orange'
  | 'pink'
  | 'purple'
  | 'darkGreen'
  | 'cyan'
  | 'crimson';

export type TabGroup = {
  id: string;
  name: string;
  type: TabGroupType;
  color: TabGroupColor;
  isCollapsed: boolean;
  tabIds: string[];
  metadata?: {
    signalId?: string;
    executionId?: string;
    findingId?: string;
  };
};

export type TabGroupingSettings = {
  autoGroupFlows: boolean;
  showGroupColors: boolean;
  collapseNewGroups: boolean;
};

export type ChatTab = {
  tabId: string;
  chatId: string;
  conversationId: string | null;
  title: string;
  applicationId?: string;
  mentions?: Array<{
    id: string;
    label: string;
    type?: 'parent' | 'child' | 'action' | 'resource';
    mentionEntity?: MentionEntity;
  }>;
  draftInput?: string;
  skillDraftId?: string;
};

type MainContentTabDataByType = {
  welcome: undefined;
  application: undefined;
  endpoint: {
    endpointId: string;
    endpointUrl?: string;
  };
  'unverified-endpoint': {
    candidateId: string;
    applicationId?: string;
  };
  domain: undefined;
  webpage: {
    id: string;
  };
  settings: undefined;
  requests: undefined;
  websocket: undefined;
  intercept: undefined;
  'match-replace': undefined;
  replay: undefined;
  'proxy-settings': undefined;
  comparer: undefined;
  executions: {
    threadId: string;
    traceId?: string;
    agentName?: string;
    executionId?: string;
  };
  'agent-execution': {
    executionId: string;
  };
  signal: {
    signalId: string;
    rejected?: boolean;
  };
  finding: {
    findingId: string;
    mode?: FindingTabMode;
  };
  interact: undefined;
  'signals-list': undefined;
  'findings-list': undefined;
  'reports-list': undefined;
  'executions-list': undefined;
  'assets-list': undefined;
  'code-list': undefined;
  'skills-list': undefined;
  skill: {
    skillId: string;
  };
  'skill-editor': {
    skillDraftId: string;
    mode: 'create' | 'edit';
    skillId?: string;
  };
  'hitl-edit': {
    toolName: string;
    args: Record<string, unknown>;
    hitlMessageId: string;
    extractedData?: unknown[];
    threadId?: string;
  };
  'node-args-viewer': {
    nodeName: string;
    nodePayload: string;
  };
  'browser-stream': {
    streamingId: string;
  };
  'http-viewer': {
    request: {
      method: string;
      url: string;
      headers: Record<string, string | string[]>;
      body?: string;
      httpVersion?: string;
    } | null;
    response: {
      status?: number;
      statusMessage?: string;
      headers?: Record<string, string | string[]>;
      body?: string;
      httpVersion?: string;
    } | null;
    fallbackOutput?: unknown;
    toolName?: string;
    title?: string;
  };
  'plan-viewer': {
    chatId: string;
    filePath: string;
  };
  'agent-executions-list': undefined;
  'co-pilot-execution': {
    threadId: string;
    title?: string | null;
    targetId?: string;
  };
  'surface-asset': {
    surfaceAssetKind: string;
    entityId: string;
    targetType: 'EXTERNAL_NETWORK' | 'INTERNAL_NETWORK';
  };
  'ai-component': {
    componentId: string;
  };
  'network-graph': undefined;
};

export type MainContentTabData = MainContentTabDataByType[MainContentTab];

type TabBase<TTabType extends MainContentTab> = {
  id: string;
  title: string;
  type: TTabType;
  isActive: boolean;
  isDirty?: boolean;
  isPinned?: boolean;
  groupId?: string;
};

type TabWithTypedData<TTabType extends MainContentTab> =
  MainContentTabDataByType[TTabType] extends undefined
    ? TabBase<TTabType> & { data?: undefined }
    : TabBase<TTabType> & { data: MainContentTabDataByType[TTabType] };

export type Tab = {
  [TabType in MainContentTab]: TabWithTypedData<TabType>;
}[MainContentTab];

type DistributiveOmit<TValue, TKey extends PropertyKey> = TValue extends unknown
  ? Omit<TValue, TKey>
  : never;

export type OpenMainContentTabInput = DistributiveOmit<Tab, 'isActive'>;
export type EndpointMainContentTab = Extract<Tab, { type: 'endpoint' }>;
export type UnverifiedEndpointMainContentTab = Extract<
  Tab,
  { type: 'unverified-endpoint' }
>;
export type WebpageMainContentTab = Extract<Tab, { type: 'webpage' }>;
export type SignalMainContentTab = Extract<Tab, { type: 'signal' }>;
export type FindingMainContentTab = Extract<Tab, { type: 'finding' }>;
export type ExecutionsMainContentTab = Extract<Tab, { type: 'executions' }>;
export type HttpViewerMainContentTab = Extract<Tab, { type: 'http-viewer' }>;
export type AgentExecutionMainContentTab = Extract<
  Tab,
  { type: 'agent-execution' }
>;
export type CoPilotExecutionMainContentTab = Extract<
  Tab,
  { type: 'co-pilot-execution' }
>;
export type SurfaceAssetMainContentTab = Extract<
  Tab,
  { type: 'surface-asset' }
>;
export type AiComponentMainContentTab = Extract<Tab, { type: 'ai-component' }>;

export type TabPageEntry = {
  [TabType in MainContentTab]: MainContentTabDataByType[TabType] extends undefined
    ? { pageId: string; title: string; type: TabType; data?: undefined }
    : {
        pageId: string;
        title: string;
        type: TabType;
        data: MainContentTabDataByType[TabType];
      };
}[MainContentTab];

export type TabNavigationState = {
  entries: TabPageEntry[];
  currentIndex: number;
};

export type GlobalNavigationEntry = {
  tabId: string;
  pageEntry: TabPageEntry;
};

export type GlobalNavigationState = {
  entries: GlobalNavigationEntry[];
  currentIndex: number;
};

export type TargetTabWorkspace = {
  tabs: Tab[];
  tabNavigationByTabId: Record<string, TabNavigationState>;
  tabHistory: string[];
  activeMainContentTabId?: string;
  tabGroups: TabGroup[];
  tabOrder: string[];
};

export const ENTITY_TAB_TYPE = {
  FINDING: 'finding',
  EXECUTIONS: 'executions',
  SIGNAL: 'signal',
  ENDPOINT: 'endpoint',
  WEBPAGE: 'webpage',
} as const satisfies Record<string, Tab['type']>;

export const buildTabPageScrollKey = ({
  tabId,
  pageId,
  pageIndex,
}: {
  tabId: string;
  pageId: string;
  pageIndex: number;
}): string => `${tabId}::${pageId}::${pageIndex}`;

export type SplitterPanelSize = {
  id: string | number;
  size: number;
  minSize?: number;
};

export type LayoutSplittersSlice = {
  // Main resizable panels sizes (tree, main, chat)
  mainPanelSizes: SplitterPanelSize[];
  globalRequestsSplitterSizes: SplitterPanelSize[];
  webSocketSplitterSizes?: [number, number];
  interactSplitterSizes?: [number, number];
  interceptSplitterSizes: SplitterPanelSize[];
  replaySplitterSizes: SplitterPanelSize[];
  replayCollectionsPanelSizes: SplitterPanelSize[];
  requestsContentSplitterSizes: SplitterPanelSize[];
  selectedRequestsContentId: string | null;
  requestViewerSplitterSizes: SplitterPanelSize[];
  matchReplaceRequestsPreviewSplitterSizes: SplitterPanelSize[];
  matchReplaceOriginalTransformedSplitterSizes: SplitterPanelSize[];
  matchReplaceRulesEditorSplitterSizes: SplitterPanelSize[];
  matchReplaceRulesPreviewSplitterSizes: SplitterPanelSize[];

  setMainPanelSizes: (sizes: SplitterPanelSize[]) => void;
  setGlobalRequestsSplitterSizes: (sizes: SplitterPanelSize[]) => void;
  setWebSocketSplitterSizes?: (sizes: [number, number]) => void;
  setInteractSplitterSizes?: (sizes: [number, number]) => void;
  setInterceptSplitterSizes: (sizes: SplitterPanelSize[]) => void;
  setReplaySplitterSizes: (sizes: SplitterPanelSize[]) => void;
  setReplayCollectionsPanelSizes: (sizes: SplitterPanelSize[]) => void;
  setRequestsContentSplitterSizes: (sizes: SplitterPanelSize[]) => void;
  setSelectedRequestsContentId: (id: string | null) => void;
  setRequestViewerSplitterSizes: (sizes: SplitterPanelSize[]) => void;
  setMatchReplaceRequestsPreviewSplitterSizes: (
    sizes: SplitterPanelSize[],
  ) => void;
  setMatchReplaceOriginalTransformedSplitterSizes: (
    sizes: SplitterPanelSize[],
  ) => void;
  setMatchReplaceRulesEditorSplitterSizes: (sizes: SplitterPanelSize[]) => void;
  setMatchReplaceRulesPreviewSplitterSizes: (
    sizes: SplitterPanelSize[],
  ) => void;
};

export type LayoutTabGroupsSlice = {
  // Chat tabs state
  chatTabs: ChatTab[];
  activeChatTabId: string | null;

  tabGroups: TabGroup[];
  tabGroupingSettings: TabGroupingSettings;

  // Chat tab actions
  setChatTabs: (tabs: ChatTab[]) => void;
  setActiveChatTabId: (tabId: string | null) => void;
  addChatTab: (tab: ChatTab) => void;
  removeChatTab: (tabId: string) => void;
  updateChatTab: (tabId: string, updates: Partial<ChatTab>) => void;
  clearChatTabs: () => void; // Clear all chat tabs (used when switching applications)

  // Tab group actions
  createTabGroup: (group: Omit<TabGroup, 'id'>) => string;
  deleteTabGroup: (groupId: string) => void;
  updateTabGroup: (
    groupId: string,
    updates: Partial<Omit<TabGroup, 'id'>>,
  ) => void;
  addTabToGroup: (tabId: string, groupId: string) => void;
  removeTabFromGroup: (tabId: string) => void;
  toggleGroupCollapse: (groupId: string) => void;
  reorderTabsInGroup: (
    groupId: string,
    fromIndex: number,
    toIndex: number,
  ) => void;
  reorderTabGroups: (fromIndex: number, toIndex: number) => void;
  setTabGroupingSettings: (settings: Partial<TabGroupingSettings>) => void;
};

export interface LayoutState
  extends LayoutSplittersSlice,
    LayoutTabGroupsSlice {
  // Sidebar state
  selectedSidebarTab: SidebarTab;

  // Main content tabs state
  mainContentTabs: Tab[];
  activeMainContentTabId: string | undefined;
  tabHistory: string[]; // Track order of tab activations for "go to previous tab" on close
  tabNavigationByTabId: Record<string, TabNavigationState>;
  globalNavigation: GlobalNavigationState;

  detachedTabIds: string[];
  isCopilotDetached: boolean;

  // Panel visibility
  showSidePanels: boolean; // controls domain tree and chat visibility

  // Individual panel collapsed state (VSCode-style minimize)
  isDomainTreeCollapsed: boolean;
  isChatCollapsed: boolean;
  isReplayCollectionsCollapsed: boolean;
  isAiSignalsKindNavCollapsed: boolean;

  isSidebarMinimized: boolean;

  // Endpoint tab state - per endpoint ID
  endpointTabs: Record<string, EndpointTab>;

  // WebPage tab state - per webpage ID
  webPageTabs: Record<string, WebPageTab>;

  tabSections: Record<string, Record<string, string>>;

  savedPinnedTabs: Record<string, TargetTabWorkspace>;

  // Actions
  setSidebarTab: (tab: SidebarTab) => void;

  // Main content tab actions
  openMainContentTab: (tab: OpenMainContentTabInput) => void;
  navigateInActiveMainContentTab: (tab: OpenMainContentTabInput) => void;
  openMainContentTabInNewInstance: (tab: OpenMainContentTabInput) => string;
  focusMainContentTabForOpenInput: (tab: OpenMainContentTabInput) => boolean;
  openOrFocusMainContentTab: (tab: OpenMainContentTabInput) => void;
  goBackInActiveMainContentTab: () => void;
  goForwardInActiveMainContentTab: () => void;
  goBackInGlobalHistory: () => void;
  goForwardInGlobalHistory: () => void;
  updateMainContentTab: (
    tabId: string,
    updates: Partial<Pick<Tab, 'title'>>,
  ) => void;
  closeMainContentTab: (tabId: string) => void;
  setActiveMainContentTab: (tabId: string) => void;
  togglePinTab: (tabId: string) => void;
  reorderMainContentTabs: (fromIndex: number, toIndex: number) => void;
  closeTargetSpecificTabs: (options?: {
    currentTargetId?: string;
    currentTargetType?: import('@terra/shared-schemas-orchestrator').TargetTypeType;
    nextTargetId?: string;
    nextTargetType?: import('@terra/shared-schemas-orchestrator').TargetTypeType;
  }) => void;
  savePinnedTabsForTarget: (targetScopeKey: string) => void;
  restorePinnedTabsForTarget: (targetScopeKey: string) => void;
  resetMainContentTabsForBoothHandoff: (
    homeTab: OpenMainContentTabInput,
  ) => void;

  markTabDetached: (tabId: string) => void;
  markTabReattached: (tabId: string) => void;
  markCopilotDetached: () => void;
  markCopilotReattached: () => void;

  // Panel visibility actions
  setSidePanelsVisible: (visible: boolean) => void;
  setDomainTreeCollapsed: (collapsed: boolean) => void;
  setChatCollapsed: (collapsed: boolean) => void;
  setReplayCollectionsCollapsed: (collapsed: boolean) => void;
  setAiSignalsKindNavCollapsed: (collapsed: boolean) => void;
  toggleDomainTreeCollapsed: () => void;
  toggleChatCollapsed: () => void;
  toggleReplayCollectionsCollapsed: () => void;
  toggleAiSignalsKindNavCollapsed: () => void;
  setSidebarMinimized: (minimized: boolean) => void;
  toggleSidebarMinimized: () => void;

  // Right panel view preference
  rightPanelView: 'collaboration' | 'copilot';
  setRightPanelView: (view: 'collaboration' | 'copilot') => void;

  // Endpoint tab actions
  setEndpointTab: (endpointId: string, tab: EndpointTab) => void;

  // WebPage tab actions
  setWebPageTab: (webPageId: string, tab: WebPageTab) => void;

  setTabSection: (args: {
    scope: string;
    tabId: string;
    sectionId: string;
  }) => void;
  clearTabSection: (args: { scope: string; tabId: string }) => void;
}
